import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { GlassButton } from '../primitives/GlassButton';

/**
 * GlassModal - Executive design system modal component
 *
 * A premium modal dialog with glass morphism effects, frosted backdrop,
 * and smooth slide-up animation.
 *
 * @example
 * ```tsx
 * <GlassModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   title="Edit Epic"
 *   size="lg"
 *   footer={
 *     <>
 *       <GlassButton variant="ghost" onClick={handleCancel}>Cancel</GlassButton>
 *       <GlassButton variant="primary" onClick={handleSave}>Save Changes</GlassButton>
 *     </>
 *   }
 * >
 *   <form>{...}</form>
 * </GlassModal>
 * ```
 */

export interface GlassModalProps {
  /** Control modal visibility */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Modal size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Modal content */
  children: React.ReactNode;
  /** Footer content (typically action buttons) */
  footer?: React.ReactNode;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close on backdrop click */
  closeOnBackdropClick?: boolean;
  /** Close on Escape key */
  closeOnEscape?: boolean;
  /** Disable body scroll when open */
  lockScroll?: boolean;
  /** Additional CSS classes for modal container */
  className?: string;
  /** Additional CSS classes for content area */
  contentClassName?: string;
  /** Icon to display next to title */
  icon?: React.ReactNode;
  /** Icon gradient variant */
  iconGradient?: 'blue-violet' | 'emerald' | 'amber' | 'rose' | 'cyan';
}

const sizeClasses = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]',
};

const iconGradientMap = {
  'blue-violet': 'bg-gradient-to-br from-blue-500 to-violet-600',
  emerald: 'bg-gradient-to-br from-emerald-400 to-emerald-600',
  amber: 'bg-gradient-to-br from-amber-400 to-amber-600',
  rose: 'bg-gradient-to-br from-rose-400 to-rose-600',
  cyan: 'bg-gradient-to-br from-cyan-400 to-cyan-600',
};

export const GlassModal: React.FC<GlassModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  lockScroll = true,
  className,
  contentClassName,
  icon,
  iconGradient = 'blue-violet',
}) => {
  // Handle escape key
  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (closeOnEscape && event.key === 'Escape') {
        onClose();
      }
    },
    [closeOnEscape, onClose]
  );

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (lockScroll && isOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
    return undefined;
  }, [isOpen, lockScroll]);

  // Add escape key listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
    return undefined;
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex items-center justify-center p-4',
        // Backdrop with blur
        'bg-slate-900/40 backdrop-blur-sm',
        // Animation
        'animate-in fade-in duration-200'
      )}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Modal container */}
      <div
        className={cn(
          'relative w-full',
          sizeClasses[size],
          // Glass effect
          'bg-[var(--glass-strong)]',
          'backdrop-blur-[24px]',
          'border border-[var(--glass-border)]',
          'rounded-2xl',
          'shadow-[var(--shadow-strong)]',
          // Animation
          'animate-in slide-in-from-bottom-4 duration-300',
          // Layout
          'flex flex-col max-h-[calc(100vh-2rem)]',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div className="flex items-start justify-between gap-4 p-6 border-b border-[var(--glass-border-subtle)]">
            <div className="flex items-center gap-3 min-w-0">
              {icon && (
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0',
                    iconGradientMap[iconGradient]
                  )}
                >
                  <div className="w-5 h-5 text-white">{icon}</div>
                </div>
              )}
              <div className="min-w-0">
                {title && (
                  <h2
                    id="modal-title"
                    className="text-lg font-semibold text-foreground truncate"
                  >
                    {title}
                  </h2>
                )}
                {subtitle && (
                  <p className="text-sm text-muted-foreground/60 mt-0.5 truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
            {showCloseButton && (
              <button
                onClick={onClose}
                className={cn(
                  'p-2 rounded-xl flex-shrink-0',
                  'text-muted-foreground/60 hover:text-foreground',
                  'hover:bg-white/10 dark:hover:bg-slate-700/30',
                  'transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-[var(--accent-blue)]/40'
                )}
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div
          className={cn(
            'flex-1 overflow-y-auto p-6',
            contentClassName
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--glass-border-subtle)]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render via portal
  return createPortal(modalContent, document.body);
};

GlassModal.displayName = 'GlassModal';

/**
 * GlassConfirmModal - Preset confirmation dialog
 */
export interface GlassConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

export const GlassConfirmModal: React.FC<GlassConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  isLoading = false,
}) => {
  const variantStyles = {
    danger: {
      gradient: 'rose' as const,
      buttonClass: 'bg-gradient-to-br from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700',
    },
    warning: {
      gradient: 'amber' as const,
      buttonClass: 'bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700',
    },
    info: {
      gradient: 'blue-violet' as const,
      buttonClass: '',
    },
  };

  const styles = variantStyles[variant];

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      iconGradient={styles.gradient}
      footer={
        <>
          <GlassButton variant="ghost" onClick={onClose} disabled={isLoading}>
            {cancelLabel}
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={onConfirm}
            isLoading={isLoading}
            className={styles.buttonClass}
          >
            {confirmLabel}
          </GlassButton>
        </>
      }
    >
      <p className="text-muted-foreground">{message}</p>
    </GlassModal>
  );
};

GlassConfirmModal.displayName = 'GlassConfirmModal';
